<template>
    <div
        class="preview-inline"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
    >
        <slot></slot>
        <v-menu
            v-model="showMenu"
            :position-x="menuX"
            :position-y="menuY"
            absolute
            offset-y
            :close-on-content-click="false"
            max-width="800"
            min-width="700"
        >
            <v-card @mouseenter="onMenuEnter" @mouseleave="onMenuLeave">
                <v-card-text class="pa-0 pb-2">
                    <div v-if="loading" class="text-center py-4">
                        <v-progress-circular
                            indeterminate
                            color="primary"
                            size="20"
                        ></v-progress-circular>
                        <div class="text-caption mt-1">{{ $translate('loading') }}</div>
                    </div>
                    <div v-else-if="items && items.length > 0">
                        <div v-if="showInfos && order" class="pa-4 pb-0">
                            <div class="d-flex justify-space-between align-center">
                                <div>
                                    <router-link
                                        :to="{
                                            name: 'providerorder',
                                            params: { providerorder_id: order.id },
                                        }"
                                        class="text-subtitle-2 font-weight-bold text-decoration-none"
                                    >
                                        {{ order.order_id }}
                                    </router-link>
                                    <template v-if="order.archived">
                                        - <i>{{ $translate('archived') }}</i>
                                    </template>
                                </div>
                                <v-chip small :color="$utils.getProviderOrderStatusColor(order)" :outlined="order.readonly">
                                    {{ $utils.getProviderOrderStatusText(order) }}
                                </v-chip>
                            </div>
                            <div class="d-flex justify-space-between mt-2 mb-2">
                                <div class="text-caption grey--text">
                                    <div v-if="order.seller_entity !== current_entity_id && order.seller_entity_data" class="mb-1">
                                        <v-icon x-small class="mr-1">mdi-store</v-icon>
                                        {{ order.seller_entity_data.name }}
                                    </div>
                                    <div v-if="order.buyer_data" class="mb-1">
                                        <v-icon x-small class="mr-1">mdi-account</v-icon>
                                        {{ order.buyer_data.name }}
                                    </div>
                                    <div v-if="order.deliverysite_data" class="mb-1">
                                        <v-icon x-small class="mr-1">mdi-map-marker</v-icon>
                                        {{ order.deliverysite_data.name }}
                                    </div>
                                    <div v-if="order.delivery_date" class="mb-1">
                                        <v-icon x-small class="mr-1">mdi-calendar</v-icon>
                                        {{ $moment(order.delivery_date).format('ll') }}
                                    </div>
                                </div>
                                <div class="text-caption grey--text text-right">
                                    <div v-if="order.total_amount_excl_vat !== null" class="mb-1">
                                        <div class="font-weight-bold">{{ $utils.price(order.total_amount_excl_vat) }}
                                            {{ $translate('excl_vat') }}</div>
                                        <div class="text-caption">{{ $utils.price(order.total_amount_incl_vat) }} {{ $translate('incl_vat') }}
                                        </div>
                                    </div>
                                    <div class="mb-1">
                                        <v-icon x-small class="mr-1">mdi-package-variant</v-icon>
                                        {{ order.items_count }} {{ $translate('items') }}
                                    </div>
                                </div>
                            </div>
                            <v-divider class="mb-2"></v-divider>
                        </div>
                        <div class="order-items-list" style="max-height: 400px; overflow-y: auto; overflow-x: auto;">
                            <v-simple-table dense class="no-wrap-table">
                                <template v-slot:default>
                                    <thead>
                                        <tr>
                                            <th class="text-left">{{ $translate('upc') }}</th>
                                            <th class="text-left">{{ $translate('product') }}</th>
                                            <th class="text-left">{{ $translate('manufacturer') }}</th>
                                            <th class="text-right">{{ $translate('quantity') }}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="item in items" :key="item.id">
                                            <td class="text-left">
                                                {{ getProductUpc(item) }}
                                            </td>
                                            <td class="text-left font-weight-bold">
                                                {{ getProductName(item) }}
                                            </td>
                                            <td class="text-left">
                                                {{ getProductManufacturer(item) }}
                                            </td>
                                            <td class="text-right">
                                                <v-chip x-small outlined>
                                                    {{ $utils.formatSaleQuantityDisplay(item) }}
                                                </v-chip>
                                            </td>
                                        </tr>
                                    </tbody>
                                </template>
                            </v-simple-table>
                        </div>
                    </div>
                    <div v-else-if="items && items.length === 0" class="text-body-2 pa-4">
                        {{ $translate('no_products_found') }}
                    </div>
                </v-card-text>
            </v-card>
        </v-menu>
    </div>
</template>

<script>
import axios from "@/plugins/axios";
import GlobalMixins from "@/mixins/global";

export default {
    name: "ProviderOrderPreview",
    mixins: [GlobalMixins],
    props: {
        orderId: {
            type: [Number, String],
            required: true,
        },
        showInfos: {
            type: Boolean,
            default: true,
        },
        delay: {
            type: Number,
            default: 1000,
        },
    },
    data() {
        return {
            items: null,
            order: null,
            loading: false,
            hoverTimer: null,
            showMenu: false,
            menuX: 0,
            menuY: 0,
        };
    },
    methods: {
        onMouseEnter(event) {
            if (this.hoverTimer) {
                clearTimeout(this.hoverTimer);
            }
            
            const rect = event.currentTarget.getBoundingClientRect();
            this.menuX = rect.left + 50;
            this.menuY = rect.bottom;
            
            this.hoverTimer = setTimeout(() => {
                this.showMenu = true;
                this.loadOrderItems();
            }, this.delay);
        },
        onMouseLeave() {
            if (this.hoverTimer) {
                clearTimeout(this.hoverTimer);
                this.hoverTimer = null;
            }
            
            this.hoverTimer = setTimeout(() => {
                this.showMenu = false;
            }, 200);
        },
        onMenuEnter() {
            if (this.hoverTimer) {
                clearTimeout(this.hoverTimer);
                this.hoverTimer = null;
            }
        },
        onMenuLeave() {
            this.showMenu = false;
        },
        async loadOrderItems() {
            if (this.items || this.loading) {
                return;
            }

            this.loading = true;

            try {
                const [itemsResponse, orderResponse] = await Promise.all([
                    axios.get(`/api/providerorderitems/?order=${this.orderId}&limit=1000`),
                    axios.get(`/api/providerorders/${this.orderId}/`)
                ]);
                this.items = itemsResponse.data.results || [];
                this.order = orderResponse.data;
            } catch (error) {
                console.error('Error loading provider order items:', error);
                this.items = [];
                this.order = null;
            } finally {
                this.loading = false;
            }
        },
        getProductUpc(item) {
            return item.catalogentry_data?.product_data?.upc || item.code || '-';
        },
        getProductName(item) {
            return item.catalogentry_data?.product_data?.name || item.label || '-';
        },
        getProductManufacturer(item) {
            return item.catalogentry_data?.product_data?.manufacturer || item.manufacturer || '-';
        },
    },
    beforeDestroy() {
        if (this.hoverTimer) {
            clearTimeout(this.hoverTimer);
        }
    },
    i18n: {
        messages: {
            en: {
                loading: "Loading...",
                upc: "UPC",
                product: "Product",
                manufacturer: "Manufacturer",
                quantity: "Quantity",
                no_products_found: "Unable to load the list of order lines",
                status: "Status",
                items: "items",
                archived: "Order archived",
                excl_vat: "HT",
                incl_vat: "TTC",
            },
            fr: {
                loading: "Chargement...",
                upc: "UPC",
                product: "Produit",
                manufacturer: "Fabricant",
                quantity: "Quantité",
                no_products_found: "Impossible de charger les lignes de commande",
                status: "Statut",
                items: "articles",
                archived: "Commande archivée",
                excl_vat: "HT",
                incl_vat: "TTC",
            },
        },
    },
};
</script>

<style scoped>
.preview-inline {
    display: inline;
}

.order-items-list {
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

.order-items-list::-webkit-scrollbar {
    width: 6px;
    height: 6px;
}

.order-items-list::-webkit-scrollbar-track {
    background: transparent;
}

.order-items-list::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
}

.order-items-list::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.3);
}

.no-wrap-table >>> td,
.no-wrap-table >>> th {
    white-space: nowrap;
}

.no-wrap-table >>> table {
    table-layout: auto;
}

.no-wrap-table >>> tbody tr:hover {
    background-color: transparent !important;
}
</style>

